<template>
  <div class="home-modern tech-home">
    <!-- ───────── HERO ───────── -->
    <section class="home-hero">
      <div class="hero-grid"></div>
      <div class="hero-light hero-light-blue"></div>
      <div class="hero-light hero-light-cyan"></div>
      <div class="hero-inner">

        <!-- LEFT COPY  -->
        <div class="hero-copy">
          <div class="hero-eyebrow">
            <span class="eyebrow-dot"></span>
            智训立方 · 情景实训平台
          </div>
          <h1>智训立方<br /><span class="h1-sub">情景交互学习平台</span></h1>
          <p class="hero-desc">
            情景化学习 · 案例式训练 · 沉浸式体验 · 过程化留痕
          </p>
          <p class="hero-desc-long">
            面向应急教学与在线培训场景，把课程、案例和训练过程转化为可进入、可体验、可复盘的在线训练资源。
          </p>
          <div class="hero-actions">
            <router-link class="primary-action" to="/more">进入学习</router-link>
            <router-link class="secondary-action" to="/more">查看案例</router-link>
          </div>
          <div class="hero-metrics">
            <div v-for="item in heroMetrics" :key="item.label" class="metric-item">
              <span class="metric-num">{{ item.value }}</span>
              <span class="metric-label">{{ item.label }}</span>
            </div>
          </div>
        </div>

        <!-- RIGHT COCKPIT -->
        <div class="hero-cockpit">
          <!-- Main scenario card -->
          <div class="cockpit-main-card">
            <div class="cmc-topbar">
              <span></span><span></span><span></span>
              <em>Scenario Console</em>
            </div>
            <div class="cmc-label">情景训练 · 案例学习 · 教学复盘</div>
            <video src="../../assets/video/bg1.mp4" autoplay muted loop playsinline class="cmc-video"></video>
            <div class="cmc-scanline"></div>
            <div class="cmc-stat-row">
              <div class="cmc-stat"><strong>360°</strong><span>情景体验</span></div>
              <div class="cmc-stat"><strong>案例</strong><span>案例资源</span></div>
              <div class="cmc-stat"><strong>Review</strong><span>训练复盘</span></div>
            </div>
          </div>

          <!-- Floating: status console -->
          <div class="cockpit-console">
            <div class="cc-title">
              <span class="cc-dot"></span>学习流程状态
            </div>
            <div class="cc-row" v-for="item in consoleRows" :key="item.name">
              <span class="cc-name">{{ item.name }}</span>
              <span :class="['cc-state', item.color]">{{ item.state }}</span>
            </div>
          </div>

          <!-- Floating: progress card 
          <div class="cockpit-progress">
            <div class="cp-label">课程完成率</div>
            <div class="cp-arc-wrap">
              <svg viewBox="0 0 80 80" class="cp-arc">
                <circle cx="40" cy="40" r="32" class="cp-track" />
                <circle cx="40" cy="40" r="32" class="cp-fill" />
              </svg>
              <span class="cp-pct">78%</span>
            </div>
            <div class="cp-meta">本期学员均值</div>
          </div>-->

          <!-- Floating: mini-tag strip -->
          <div class="cockpit-tags">
            <span class="ct-tag green"><i class="el-icon-check"></i> 在线</span>
            <span class="ct-tag blue"><i class="el-icon-video-play"></i> 训练中</span>
            <span class="ct-tag orange"><i class="el-icon-edit"></i> 留痕</span>
          </div>

          <!-- Connector lines (pure CSS) -->
          <div class="cockpit-line line-a"></div>
          <div class="cockpit-line line-b"></div>
        </div>

      </div>
    </section>

    <!-- ───────── CASES ───────── -->
    <section class="home-section cases-section">
      <div class="section-head cases-head">
        <div>
          <div class="section-kicker">Classic Cases</div>
          <h2>经典案例</h2>
        </div>
        <router-link class="more-link" to="/more">查看全部案例 →</router-link>
      </div>

      <div v-if="courses.length" class="home-cases-grid">
        <article
          class="home-case-card modern-card"
          v-for="course in courses"
          :key="course.id || course.urlCode || course.title"
          @click="handleDetail(course)"
        >
          <div class="case-cover">
            <img :src="baseUrl + course.imgUrl" :alt="course.title || '案例封面'" />
            <span class="case-badge">情景案例</span>
          </div>
          <div class="case-body">
            <h3>{{ course.title }}</h3>
            <p>{{ course.summary || '围绕典型情景开展案例学习、任务理解和训练复盘。' }}</p>
            <div class="case-footer">
              <span>进入学习</span>
              <i class="el-icon-right"></i>
            </div>
          </div>
        </article>
      </div>
      <div v-else class="empty-card modern-card">
        <i class="el-icon-folder-opened"></i>
        <p>案例资源加载中，或当前暂无公开案例。</p>
      </div>
    </section>


    <!-- ───────── PLATFORM VALUE ───────── -->
    <section class="home-section home-intro">
      <div class="section-head section-head-clean">
        <div>
          <h2>情景训练升级</h2>
          <p class="section-subtitle">从“看课件”升级为“进入情景训练”，让课程、案例和任务变成可进入、可体验、可复盘的学习过程。</p>
        </div>
      </div>
      <div class="value-grid">
        <div class="value-card modern-card" v-for="(item, idx) in values" :key="item.title">
          <div class="value-card-accent" :style="{ '--accent-idx': idx }"></div>
          <div class="value-icon"><i :class="item.icon"></i></div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.desc }}</p>
          <div class="value-card-num">0{{ idx + 1 }}</div>
        </div>
      </div>
    </section>

    <!-- ───────── WORKFLOW ───────── -->
    <section class="home-section capability-section">
      <div class="capability-panel modern-card">
        <div class="capability-tech-bg"></div>
        <div class="cap-dots"></div>
        <div class="capability-copy">
          <div class="section-kicker dark-kicker">Training Workflow</div>
          <h2>围绕课程与案例形成完整学习闭环</h2>
          <p>
            平台支撑教师组织课程、学员进入案例、完成情景学习，并为后续复盘、讲评和培训资料建设提供基础。
          </p>
          <div class="cap-data-strip">
            <div v-for="d in capData" :key="d.label" class="cap-data-item">
              <strong>{{ d.value }}</strong>
              <span>{{ d.label }}</span>
            </div>
          </div>
        </div>
        <div class="workflow-list">
          <div class="workflow-item" v-for="(step, index) in workflow" :key="step.title">
            <div class="wi-num">{{ String(index + 1).padStart(2, "0") }}</div>
            <div class="wi-body">
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
            <div v-if="index < workflow.length - 1" class="wi-connector"></div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { homePageCards } from "@/api/index";
import { mapState } from "vuex";

export default {
  name: "HomePage",
  data() {
    return {
      heroMetrics: [
        { value: "01", label: "课程组织" },
        { value: "02", label: "案例进入" },
        { value: "03", label: "情景体验" },
        { value: "04", label: "复盘提升" },
      ],
      consoleRows: [
        { name: "课程入口", state: "准备就绪", color: "state-ready" },
        { name: "案例资源", state: "在线", color: "state-online" },
        { name: "训练记录", state: "追踪", color: "state-trace" },
      ],
      capData: [
        { value: "4步", label: "完整闭环" },
        { value: "多角色", label: "分层使用" },
        { value: "全过程", label: "留痕追踪" },
      ],
      values: [
        {
          title: "情景化学习",
          desc: "通过三维、全景、图文和交互内容提升案例理解效率。",
          icon: "el-icon-data-board",
        },
        {
          title: "案例式训练",
          desc: "围绕典型应急案例组织学习、任务和课堂讨论。",
          icon: "el-icon-collection",
        },
        {
          title: "课程组织支持",
          desc: "面向教师、学员和管理员提供不同角色使用入口。",
          icon: "el-icon-reading",
        },
        {
          title: "训练复盘基础",
          desc: "为学习记录、过程回看和后续复盘讲评留下数据基础。",
          icon: "el-icon-document-checked",
        },
      ],
      workflow: [
        { title: "案例进入", desc: "从经典案例或课程入口进入训练资源。" },
        { title: "情景理解", desc: "通过场景、文字、图片和交互任务理解处置背景。" },
        { title: "任务学习", desc: "围绕岗位、流程和关键问题完成学习训练。" },
        { title: "复盘提升", desc: "结合学习记录和讲评要点形成培训改进闭环。" },
      ],
      courses: [],
    };
  },
  computed: {
    ...mapState(["baseUrl"]),
  },
  mounted() {
    this.getHomePageCards();
  },
  methods: {
    getHomePageCards() {
      homePageCards()
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          this.courses = list.slice(0, 4);
        })
        .catch((error) => {
          console.error("获取首页案例失败:", error);
          this.courses = [];
        });
    },
    handleDetail(course) {
      if (!course || !course.urlCode) return;
      this.$router.push({
        name: "StudentClass",
        params: { id: course.urlCode },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ─────────────────────────────────────────
   CSS Variables (local supplement)
───────────────────────────────────────── */
.home-modern {
  --blue-deep: #081c42;
  --blue-mid:  #1557d8;
  --blue-act:  #1e9bff;
  --cyan:      #20c8ff;
  --gray-body: #475569;
  --gray-soft: #64748b;
  --card-bg:   rgba(255,255,255,0.88);
  --card-bdr:  rgba(168,199,245,0.72);
  overflow: hidden;
  background: linear-gradient(180deg, #f4f8ff 0%, #ffffff 38%, #f8fafc 100%);
}

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
.home-hero {
  position: relative;
  min-height: calc(100vh - 78px);
  display: flex;
  align-items: center;
  padding: 64px 0 58px;
  background:
    radial-gradient(ellipse at 6% 10%, rgba(21,87,216,0.16), transparent 38%),
    radial-gradient(ellipse at 90% 16%, rgba(32,200,255,0.13), transparent 32%),
    linear-gradient(150deg, #f2f7ff 0%, #eaf3ff 48%, #ffffff 100%);
}

.hero-grid {
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background-image:
    linear-gradient(rgba(47,111,237,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(47,111,237,0.08) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.9), transparent 94%);
  pointer-events: none;
}

.hero-light {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(14px);
}
.hero-light-blue {
  width: 500px; height: 500px;
  left: -180px; bottom: -200px;
  background: rgba(21,87,216,0.10);
}
.hero-light-cyan {
  width: 360px; height: 360px;
  right: 2%; top: 4%;
  background: rgba(32,200,255,0.10);
}

.hero-inner,
.home-section {
  width: min(1400px, calc(100% - 48px));
  margin: 0 auto;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0,1fr) minmax(520px,0.96fr);
  align-items: center;
  gap: 52px;
}

/* Left copy */
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(21,87,216,0.09);
  color: var(--blue-mid);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.eyebrow-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 0 5px rgba(32,200,255,0.18), 0 0 14px rgba(32,200,255,0.7);
}

.hero-copy h1 {
  margin: 18px 0 14px;
  color: var(--blue-deep);
  font-size: clamp(34px, 4vw, 52px);
  line-height: 1.1;
  font-weight: 950;
  letter-spacing: -1px;
}
.h1-sub {
  display: block;
  font-size: clamp(22px, 2.8vw, 36px);
  font-weight: 700;
  color: var(--blue-mid);
  letter-spacing: -0.5px;
  opacity: 0.92;
}

.hero-desc {
  color: var(--blue-mid);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
  opacity: 0.82;
}
.hero-desc-long {
  max-width: 620px;
  color: var(--gray-body);
  font-size: 15px;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 28px;
  flex-wrap: wrap;
}
.primary-action,
.secondary-action,
.more-link {
  text-decoration: none;
  font-weight: 800;
  transition: all 0.22s ease;
}
.primary-action,
.secondary-action {
  min-width: 132px;
  height: 40px;
  padding: 0 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.primary-action {
  color: #fff;
  background: linear-gradient(135deg, #1248c8, #1e9bff 56%, #20c8ff);
  box-shadow: 0 12px 28px rgba(21,87,216,0.26);
}
.primary-action:hover { transform: translateY(-2px); box-shadow: 0 18px 36px rgba(21,87,216,0.34); }
.secondary-action {
  color: var(--blue-deep);
  background: rgba(255,255,255,0.88);
  border: 1px solid rgba(21,87,216,0.18);
  backdrop-filter: blur(10px);
}
.secondary-action:hover { transform: translateY(-2px); border-color: var(--blue-act); }

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap: 10px;
  max-width: 640px;
  margin-top: 28px;
}
.metric-item {
  padding: 12px 10px;
  border-radius: 14px;
  background: rgba(255,255,255,0.78);
  border: 1px solid rgba(200,218,248,0.9);
  box-shadow: 0 8px 22px rgba(15,47,95,0.06);
  backdrop-filter: blur(10px);
  text-align: center;
}
.metric-num {
  display: block;
  color: var(--blue-mid);
  font-size: 20px;
  font-weight: 950;
  line-height: 1;
}
.metric-label {
  display: block;
  margin-top: 6px;
  color: var(--gray-soft);
  font-size: 12px;
  font-weight: 700;
}

/* ── RIGHT COCKPIT ── */
.hero-cockpit {
  position: relative;
  min-height: 468px;
}

/* Main scenario card */
.cockpit-main-card {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(148,186,248,0.78);
  background: linear-gradient(145deg, rgba(255,255,255,0.96), rgba(232,244,255,0.88));
  box-shadow: 0 28px 60px rgba(8,28,66,0.10), 0 1px 0 rgba(255,255,255,0.9) inset;
  padding: 12px;
  min-height: 380px;
}
.cmc-topbar {
  position: absolute;
  z-index: 4;
  top: 20px; left: 22px; right: 22px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255,255,255,0.88);
  font-size: 12px;
  font-weight: 800;
  pointer-events: none;
}
.cmc-topbar span {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.6);
}
.cmc-topbar em {
  margin-left: auto;
  font-style: normal;
  letter-spacing: 0.06em;
  font-size: 11px;
}
.cmc-label {
  position: absolute;
  z-index: 4;
  top: 52px; left: 26px;
  padding: 5px 10px;
  border-radius: 999px;
  color: #fff;
  background: rgba(8,28,66,0.68);
  backdrop-filter: blur(10px);
  font-size: 12px;
  font-weight: 800;
}
.cmc-video {
  width: 100%;
  min-height: 380px;
  object-fit: cover;
  border-radius: 16px;
  display: block;
  filter: saturate(1.06) contrast(1.05);
}
.cmc-scanline {
  position: absolute;
  inset: 12px;
  border-radius: 16px;
  background:
    linear-gradient(180deg, rgba(32,200,255,0.08), transparent 30%, rgba(8,28,66,0.22)),
    repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 8px);
  pointer-events: none;
}
.cmc-stat-row {
  position: absolute;
  z-index: 4;
  left: 26px; right: 26px; bottom: 24px;
  display: grid;
  grid-template-columns: repeat(3,1fr);
  gap: 10px;
}
.cmc-stat {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(8,20,48,0.62);
  border: 1px solid rgba(255,255,255,0.12);
  backdrop-filter: blur(14px);
  color: #fff;
}
.cmc-stat strong { display: block; font-size: 18px; line-height: 1; }
.cmc-stat span   { display: block; margin-top: 6px; font-size: 11px; opacity: 0.85; }

/* Floating status console */
.cockpit-console {
  position: absolute;
  z-index: 6;
  right: -14px;
  top: 72px;
  width: 178px;
  padding: 13px 14px;
  border-radius: 16px;
  background: rgba(8,22,56,0.82);
  border: 1px solid rgba(80,180,255,0.28);
  backdrop-filter: blur(16px);
  box-shadow: 0 16px 36px rgba(4,16,44,0.32), 0 1px 0 rgba(255,255,255,0.07) inset;
  color: #fff;
}
.cc-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  color: rgba(255,255,255,0.72);
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}
.cc-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(32,200,255,0.8);
}
.cc-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-top: 1px solid rgba(255,255,255,0.09);
  font-size: 12px;
}
.cc-name { color: rgba(255,255,255,0.82); }
.cc-state { font-weight: 800; font-size: 11px; }
.state-ready  { color: #20c8ff; }
.state-online { color: #4ade80; }
.state-trace  { color: #fbbf24; }

/* Floating progress card */
.cockpit-progress {
  position: absolute;
  z-index: 6;
  left: -16px;
  bottom: 80px;
  width: 132px;
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255,255,255,0.94);
  border: 1px solid rgba(148,186,248,0.72);
  box-shadow: 0 16px 36px rgba(8,28,66,0.12);
  text-align: center;
}
.cp-label {
  font-size: 11px;
  font-weight: 800;
  color: var(--gray-soft);
  margin-bottom: 8px;
}
.cp-arc-wrap {
  position: relative;
  width: 72px;
  margin: 0 auto 6px;
}
.cp-arc {
  width: 72px; height: 72px;
  transform: rotate(-90deg);
}
.cp-track {
  fill: none;
  stroke: rgba(21,87,216,0.12);
  stroke-width: 7;
}
.cp-fill {
  fill: none;
  stroke: url(#progGrad);
  stroke-width: 7;
  stroke-linecap: round;
  stroke-dasharray: 201;
  stroke-dashoffset: 44; /* 78% = 201*0.22 ≈ 44 */
  transition: stroke-dashoffset 1.2s ease;
}
.cp-pct {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  font-weight: 950;
  color: var(--blue-mid);
}
.cp-meta { font-size: 10px; color: var(--gray-soft); }

/* Tag strip */
.cockpit-tags {
  position: absolute;
  z-index: 6;
  right: -14px;
  bottom: 110px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ct-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  backdrop-filter: blur(10px);
}
.ct-tag.green  { background: rgba(74,222,128,0.16); color: #16a34a; border: 1px solid rgba(74,222,128,0.38); }
.ct-tag.blue   { background: rgba(30,155,255,0.14); color: #1248c8; border: 1px solid rgba(30,155,255,0.34); }
.ct-tag.orange { background: rgba(251,191,36,0.14); color: #b45309; border: 1px solid rgba(251,191,36,0.34); }

/* Connector lines */
.cockpit-line {
  position: absolute;
  z-index: 5;
  background: linear-gradient(90deg, transparent, rgba(32,200,255,0.38), transparent);
  pointer-events: none;
}
.line-a { width: 1px; height: 60px; right: -14px; top: 32px; background: linear-gradient(180deg, transparent, rgba(32,200,255,0.38), transparent); }
.line-b { width: 1px; height: 48px; left: -16px; bottom: 140px; background: linear-gradient(180deg, transparent, rgba(21,87,216,0.3), transparent); }

/* SVG gradient def (inline trick via CSS won't work, use SVG filter or just the arc color via stroke) */

/* ─────────────────────────────────────────
   SECTIONS COMMON
───────────────────────────────────────── */
.home-section { padding: 72px 0; }

.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(21,87,216,0.09);
  color: var(--blue-mid);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
.section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}
.section-head h2 {
  margin-top: 14px;
  color: var(--blue-deep);
  font-size: 28px;
  line-height: 1.22;
  font-weight: 950;
}
.section-head p {
  max-width: 560px;
  color: var(--gray-body);
  font-size: 14px;
  line-height: 1.85;
}

/* ─────────────────────────────────────────
   VALUE CARDS
───────────────────────────────────────── */
.value-grid {
  display: grid;
  grid-template-columns: repeat(4,1fr);
  gap: 20px;
}
.value-card {
  position: relative;
  overflow: hidden;
  padding: 24px 20px 20px;
  transition: all 0.24s ease;
  background: rgba(255,255,255,0.9);
  border: 1px solid var(--card-bdr);
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(8,28,66,0.06);
}
/* Top accent bar per card */
.value-card-accent {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
  background: linear-gradient(90deg,
    hsl(calc(210 + var(--accent-idx) * 18), 88%, 52%),
    rgba(32,200,255,0));
}
.value-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 22px 52px rgba(8,28,66,0.11);
  border-color: rgba(30,155,255,0.36);
}
.value-icon {
  width: 44px; height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: var(--blue-mid);
  background: linear-gradient(145deg, rgba(21,87,216,0.10), rgba(32,200,255,0.12));
  font-size: 20px;
  margin-bottom: 16px;
}
.value-card h3 {
  color: var(--blue-deep);
  font-size: 16px;
  font-weight: 900;
  margin-bottom: 8px;
}
.value-card p {
  color: var(--gray-body);
  font-size: 13px;
  line-height: 1.75;
}
.value-card-num {
  position: absolute;
  right: 18px; bottom: 14px;
  font-size: 38px;
  font-weight: 950;
  color: rgba(21,87,216,0.06);
  line-height: 1;
  pointer-events: none;
  user-select: none;
}

/* ─────────────────────────────────────────
   CAPABILITY / WORKFLOW
───────────────────────────────────────── */
.capability-section { padding: 0 0 72px; }

.capability-panel {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 0.84fr 1.16fr;
  gap: 40px;
  padding: 36px 36px 36px 36px;
  background: linear-gradient(135deg, rgba(6,22,58,0.98), rgba(10,52,112,0.94));
  border-radius: 24px;
  border: 1px solid rgba(30,120,255,0.22);
  box-shadow: 0 32px 64px rgba(4,14,40,0.18);
}
.capability-tech-bg {
  position: absolute;
  inset: 0;
  opacity: 0.38;
  background-image:
    radial-gradient(circle at 22% 28%, rgba(32,200,255,0.26), transparent 28%),
    linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px);
  background-size: auto, 36px 36px, 36px 36px;
  pointer-events: none;
}
/* Decorative dot cluster */
.cap-dots {
  position: absolute;
  right: 36px; top: 36px;
  width: 80px; height: 80px;
  background-image: radial-gradient(circle, rgba(32,200,255,0.28) 1.5px, transparent 1.5px);
  background-size: 14px 14px;
  pointer-events: none;
}
.capability-copy,
.workflow-list {
  position: relative;
  z-index: 1;
}
.dark-kicker {
  color: #c4f0ff;
  background: rgba(255,255,255,0.10);
}
.capability-copy h2 {
  margin: 16px 0 12px;
  color: #fff;
  font-size: 24px;
  line-height: 1.28;
  font-weight: 950;
}
.capability-copy p {
  color: rgba(255,255,255,0.74);
  font-size: 14px;
  line-height: 1.88;
}
.cap-data-strip {
  display: flex;
  gap: 24px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.10);
}
.cap-data-item strong {
  display: block;
  color: var(--cyan);
  font-size: 18px;
  font-weight: 950;
  line-height: 1;
}
.cap-data-item span {
  display: block;
  margin-top: 5px;
  color: rgba(255,255,255,0.6);
  font-size: 11px;
  font-weight: 700;
}

.workflow-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.workflow-item {
  position: relative;
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.11);
  backdrop-filter: blur(8px);
  margin-bottom: 8px;
  transition: background 0.2s;
}
.workflow-item:hover { background: rgba(255,255,255,0.11); }
.wi-num {
  width: 42px; height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  align-self: start;
  color: #fff;
  background: linear-gradient(135deg, var(--blue-mid), var(--cyan));
  font-weight: 950;
  font-size: 14px;
  box-shadow: 0 10px 22px rgba(32,200,255,0.20);
}
.wi-body h3 { color: #fff; font-size: 15px; font-weight: 900; margin-bottom: 5px; }
.wi-body p  { color: rgba(255,255,255,0.68); font-size: 13px; line-height: 1.7; }
/* connector line between items */
.wi-connector {
  position: absolute;
  left: 34px;
  bottom: -8px;
  width: 1px;
  height: 8px;
  background: linear-gradient(180deg, rgba(32,200,255,0.36), transparent);
  z-index: 2;
}

/* ─────────────────────────────────────────
   CASES
───────────────────────────────────────── */
.cases-head { align-items: center; }
.more-link  { color: var(--blue-mid); font-weight: 800; text-decoration: none; font-size: 14px; }
.more-link:hover { color: var(--cyan); }

.home-cases-grid {
  display: grid;
  grid-template-columns: repeat(4,1fr);
  gap: 20px;
}
.home-case-card {
  overflow: hidden;
  cursor: pointer;
  transition: all 0.24s ease;
  background: rgba(255,255,255,0.9);
  border: 1px solid var(--card-bdr);
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(8,28,66,0.06);
}
.home-case-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 22px 52px rgba(8,28,66,0.11);
  border-color: rgba(30,155,255,0.36);
}
.case-cover {
  position: relative;
  height: 168px;
  overflow: hidden;
  background: #e4edf8;
}
.case-cover::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(8,28,66,0.04), rgba(8,28,66,0.22));
  pointer-events: none;
}
.case-cover img {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.36s ease;
}
.home-case-card:hover .case-cover img { transform: scale(1.06); }
.case-badge {
  position: absolute;
  z-index: 1;
  left: 12px; top: 12px;
  padding: 4px 9px;
  border-radius: 999px;
  color: #fff;
  background: rgba(18,72,200,0.90);
  font-size: 11px;
  font-weight: 800;
}
.case-body { padding: 16px 16px 14px; }
.case-body h3 {
  color: var(--blue-deep);
  font-size: 15px;
  font-weight: 900;
  margin-bottom: 8px;
  min-height: 42px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.case-body p {
  color: var(--gray-body);
  font-size: 13px;
  line-height: 1.72;
  min-height: 64px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.case-footer {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--blue-mid);
  font-weight: 800;
  font-size: 13px;
}

.empty-card {
  padding: 28px;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--gray-soft);
  background: rgba(255,255,255,0.9);
  border: 1px solid var(--card-bdr);
  border-radius: 20px;
}
.empty-card i { color: var(--blue-act); font-size: 24px; }

/* ─────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────── */
@media (max-width: 1180px) {
  .hero-inner,
  .capability-panel {
    grid-template-columns: 1fr;
  }
  .hero-cockpit { min-height: 360px; }
  .value-grid,
  .home-cases-grid { grid-template-columns: repeat(2,1fr); }
  .cockpit-console { right: 0; top: 64px; }
  .cockpit-tags    { right: 0; }
  .cockpit-progress { left: 0; }
}

@media (max-width: 720px) {
  .hero-inner,
  .home-section {
    width: calc(100% - 28px);
  }
  .home-hero { padding: 44px 0; }
  .hero-metrics { grid-template-columns: repeat(2,1fr); }
  .cockpit-console,
  .cockpit-progress,
  .cockpit-tags { display: none; }
  .cmc-video { min-height: 300px; }
  .cockpit-main-card { min-height: 300px; }
  .home-section { padding: 52px 0; }
  .section-head { display: block; }
  .section-head h2,
  .capability-copy h2 { font-size: 22px; }
  .value-grid,
  .home-cases-grid { grid-template-columns: 1fr; }
  .capability-panel { padding: 22px 20px; }
  .cap-dots { display: none; }
}

.section-subtitle {
  max-width: 760px;
  margin: 14px 0 0;
  font-size: 15px;
  line-height: 1.8;
  color: #5f6f89;
  font-weight: 500;
}

@media (max-width: 768px) {
  .section-subtitle {
    font-size: 13px;
    line-height: 1.7;
  }
}

</style>

